import type { ApprovalStatus, DecisionSource, DecisionType } from '@cadencia/contracts';
import { APPROVAL_EXPIRY_DAYS, type ChangeClassification } from './change-policy.js';

/**
 * Forma canonica de una decision del motor (docs/DATA_MODEL.md,
 * `training_decisions`; requisito §62).
 *
 * Aqui NO hay nada de entrenamiento: son los campos comunes a cualquier
 * decision, para que la Fase 4 pueda registrar decisiones de nutricion sin
 * duplicar este vocabulario. Los adaptadores de cada fuente viven junto a esa
 * fuente (`training/proposals.ts`).
 *
 * Sin reloj: la fecha entra como parametro. Es lo que permite probar la
 * caducidad sin esperar siete dias (ADR-007).
 */

/**
 * Los tres vocabularios se declaran en `@cadencia/contracts` porque se guardan
 * en columnas con `check`. Se reexportan aqui para que el dominio siga siendo
 * el sitio natural donde buscarlos.
 */
export type { ApprovalStatus, DecisionSource, DecisionType };

export interface DecisionDraft {
  decisionType: DecisionType;
  /** Entradas resumidas que llevaron a decidir. No el historial completo. */
  inputs: Record<string, unknown>;
  previousValue: unknown;
  newValue: unknown;
  source: DecisionSource;
  /** Motivo ESTRUCTURADO. La prosa es opcional y va aparte. */
  reasonCodes: string[];
  reasonText: string | null;
  /** 0-1. Cuanto de firme es la señal que la dispara. */
  confidence: number;
  classification: ChangeClassification;
}

export interface DecisionRecord extends Omit<DecisionDraft, 'classification'> {
  requiresApproval: boolean;
  approvalStatus: ApprovalStatus;
  decidedAt: string;
  /** Fecha limite para aprobar. `null` cuando no hace falta aprobacion. */
  expiresOn: string | null;
}

/** Dias exactos, en UTC, sin depender de la zona del dispositivo. */
export function addDaysIso(iso: string, days: number): string {
  const fecha = new Date(iso);
  if (Number.isNaN(fecha.getTime())) throw new Error(`Fecha no valida: ${iso}`);
  fecha.setUTCDate(fecha.getUTCDate() + days);
  return fecha.toISOString();
}

/**
 * Fecha limite para aprobar. Se calcula, no se guarda: es la unica formula, y
 * la usan por igual quien crea la decision y quien la lee de la base de datos.
 * Dos calculos separados del mismo dato acaban discrepando.
 */
export function expiryFor(decidedAt: string, requiresApproval: boolean): string | null {
  return requiresApproval ? addDaysIso(decidedAt, APPROVAL_EXPIRY_DAYS) : null;
}

/**
 * Convierte un borrador en decision registrable.
 *
 * `approvalStatus` se deriva de la clasificacion y no se acepta como
 * parametro: si quien llama pudiera declararlo, podria marcar como
 * `not_required` algo que la politica central considera relevante, y la
 * aprobacion dejaria de ser una garantia.
 */
export function newDecision(draft: DecisionDraft, decidedAt: string): DecisionRecord {
  const requiresApproval = draft.classification.requiresApproval;
  const { classification, ...resto } = draft;

  return {
    ...resto,
    // Redondeo a dos decimales: la columna es numeric(3,2).
    confidence: Math.round(Math.min(Math.max(draft.confidence, 0), 1) * 100) / 100,
    reasonCodes: [...draft.reasonCodes, ...classification.reasonCodes],
    requiresApproval,
    approvalStatus: requiresApproval ? 'pending' : 'not_required',
    decidedAt,
    expiresOn: expiryFor(decidedAt, requiresApproval),
  };
}

/**
 * Una decision pendiente caduca a los 7 dias y el plan sigue sin aplicarla
 * (docs/TRAINING_ENGINE.md §10).
 *
 * Solo caducan las PENDIENTES: una ya aprobada o rechazada es historia, y
 * reescribirla borraria la unica prueba de que el usuario decidio algo.
 */
export function isExpired(decision: DecisionRecord, nowIso: string): boolean {
  if (decision.approvalStatus !== 'pending') return false;
  if (decision.expiresOn === null) return false;
  return new Date(nowIso).getTime() >= new Date(decision.expiresOn).getTime();
}

export function expireIfDue(decision: DecisionRecord, nowIso: string): DecisionRecord {
  return isExpired(decision, nowIso) ? { ...decision, approvalStatus: 'expired' } : decision;
}

/** Las que esperan respuesta del usuario ahora mismo. */
export function pendingDecisions(
  decisions: readonly DecisionRecord[],
  nowIso: string,
): DecisionRecord[] {
  return decisions.filter(
    (decision) => decision.approvalStatus === 'pending' && !isExpired(decision, nowIso),
  );
}
